/**
 * rehypeCitation — Rehype plugins that transform citation markers
 * in text nodes into <cite> elements.
 *
 * Two variants:
 *   rehypeCitation        — Lib Chat: [n] (1-99) → <cite dataCitation="n" dataCitationMode="source">
 *   rehypeArticleCitation — Article Chat: [¶n] (0-999999) → <cite dataCitation="n" dataCitationMode="block">
 *
 * Works at the HTML AST level (after markdown → HTML conversion), so it
 * doesn't interfere with markdown parsing.
 *
 * Usage with react-markdown:
 *   rehypePlugins={[rehypeCitation]}         // Lib Chat
 *   rehypePlugins={[rehypeArticleCitation]}  // Article Chat
 *   components={{ cite: CitationRenderer }}
 */

// Skip transformation inside code/pre elements — [1] in code blocks is array syntax, not citations
const SKIP_TAGS = new Set(['code', 'pre']);

/**
 * Shared tree walker parameterized by pattern and citation mode.
 * @param {RegExp} pattern — must have a single capture group for the number
 * @param {string} mode — 'source' or 'block'
 */
function walkTree(node, pattern, mode) {
  if (!node.children) return;

  // Don't recurse into code/pre elements
  if (node.type === 'element' && SKIP_TAGS.has(node.tagName)) return;

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];

    if (child.type === 'text') {
      const text = child.value;
      pattern.lastIndex = 0;

      if (!pattern.test(text)) {
        continue;
      }

      // Reset and split
      pattern.lastIndex = 0;
      const newNodes = [];
      let lastIndex = 0;
      let match;

      while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          newNodes.push({
            type: 'text',
            value: text.slice(lastIndex, match.index),
          });
        }

        newNodes.push({
          type: 'element',
          tagName: 'cite',
          properties: { 'dataCitation': match[1], 'dataCitationMode': mode },
          children: [{ type: 'text', value: match[1] }],
        });

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        newNodes.push({
          type: 'text',
          value: text.slice(lastIndex),
        });
      }

      if (newNodes.length > 0) {
        node.children.splice(i, 1, ...newNodes);
        i += newNodes.length - 1; // adjust index for spliced nodes
      }
    } else {
      walkTree(child, pattern, mode);
    }
  }
}

/** Lib Chat citations: [1], [2], ... [999] */
const SOURCE_PATTERN = /\[(\d{1,3})\]/g;

/** Article Chat citations: [¶0], [¶1], ... [¶999999]
 * Also matches range notation [¶N-¶M] — captures the start paragraph (N),
 * so range citations navigate to the first referenced paragraph.
 */
const BLOCK_PATTERN = /\[¶(\d{1,6})(?:-¶\d{1,6})?\]/g;

/** rehypeCitation — transforms [n] markers (Lib Chat source citations) */
export default function rehypeCitation() {
  return (tree) => {
    walkTree(tree, SOURCE_PATTERN, 'source');
  };
}

/** rehypeArticleCitation — transforms [¶n] markers (Article Chat block citations) */
export function rehypeArticleCitation() {
  return (tree) => {
    walkTree(tree, BLOCK_PATTERN, 'block');
  };
}
